import {
    Alert, Box, Button, CircularProgress, Container, Divider,
    Grid, IconButton, InputAdornment, Snackbar, Tab, TablePagination, Tabs, TextField, Typography
} from '@mui/material';
import { ArrowBack, Search } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import moment from 'moment';
import ApiServices from '../../services/ApiServices';
import GatewayOtaForm from './GatewayOtaForm';
import GwSelfOtaForm from './GwSelfOtaForm';
import GatewayOtaProgress from './GatewayOtaProgress';
import GatewaySessionHistory from './GatewaySessionHistory';
import GwOtaProgress from './GwOtaProgress';
import GwOtaHistory from './GwOtaHistory';

const DEVICE_COLUMNS = [
    { field: 'deviceMac', headerName: 'Device MAC', flex: 1, sortable: false },
    { field: 'routerVersion', headerName: 'Router Ver.', flex: 0.7, sortable: false },
    { field: 'masterVersion', headerName: 'Master Ver.', flex: 0.7, sortable: false },
    {
        field: 'lastSeen', headerName: 'Last Seen', flex: 1, sortable: false,
        renderCell: (params) => params.row.lastSeen
            ? moment(params.row.lastSeen).format('DD-MM-YYYY HH:mm:ss')
            : 'N/A'
    },
];

function InfoRow({ label, value }) {
    return (
        <>
            <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
            </Grid>
            <Grid item xs={8}>
                <Typography variant="body2">{value ?? 'N/A'}</Typography>
            </Grid>
        </>
    );
}

function GatewayDetail({ gatewayId, onBack }) {
    const [gateway, setGateway] = useState(null);
    const [deviceCount, setDeviceCount] = useState(0);
    const [loadingGateway, setLoadingGateway] = useState(true);

    const [devices, setDevices] = useState([]);
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [devicePage, setDevicePage] = useState(0);
    const [devicePageSize, setDevicePageSize] = useState(10);
    const [deviceTotal, setDeviceTotal] = useState(0);

    const [macInput, setMacInput] = useState('');
    const [macFilter, setMacFilter] = useState([]);

    const [tab, setTab] = useState(0);
    const [otaMode, setOtaMode] = useState('devices');

    const [alertObj, setAlertObj] = useState({ open: false, severity: 'error', message: '' });

    useEffect(() => {
        ApiServices.getGateway(gatewayId)
            .then(response => {
                setGateway(response.data.gateway);
                setDeviceCount(response.data.deviceCount);
            })
            .catch(error => {
                setAlertObj({ open: true, severity: 'error', message: error.response?.data ?? error.message });
            })
            .finally(() => setLoadingGateway(false));
    }, [gatewayId]);

    const fetchDevices = (page = devicePage, pageSize = devicePageSize, macs = macFilter) => {
        setLoadingDevices(true);
        ApiServices.getGatewayDevices(gatewayId, page, pageSize, macs)
            .then(response => {
                if (response.status === 204 || !response.data) {
                    setDevices([]);
                    setDeviceTotal(0);
                } else {
                    setDevices(response.data.content ?? []);
                    setDeviceTotal(response.data.totalElements ?? 0);
                }
            })
            .catch(error => {
                setAlertObj({ open: true, severity: 'error', message: error.response?.data ?? error.message });
            })
            .finally(() => setLoadingDevices(false));
    };

    useEffect(() => {
        fetchDevices();
    }, [devicePage, devicePageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleMacSearch = () => {
        const macs = macInput.split(',').map(s => s.trim()).filter(Boolean);
        setMacFilter(macs);
        setDevicePage(0);
        fetchDevices(0, devicePageSize, macs);
    };

    const handleMacClear = () => {
        setMacInput('');
        setMacFilter([]);
        setDevicePage(0);
        fetchDevices(0, devicePageSize, []);
    };

    if (loadingGateway) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 3, mb: 4 }}>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={3000}
                open={alertObj.open}
                onClose={() => setAlertObj(a => ({ ...a, open: false }))}
            >
                <Alert severity={alertObj.severity} sx={{ width: '100%' }}>{alertObj.message}</Alert>
            </Snackbar>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <IconButton onClick={onBack} size="small">
                    <ArrowBack />
                </IconButton>
                <Typography variant="h6">Gateway: {gatewayId}</Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={3}>
                <Grid item xs={12} md={tab === 0 ? 4 : 12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Gateway Info</Typography>
                    {gateway && (
                        <Grid container spacing={0.5}>
                            <InfoRow label="Gateway ID" value={gateway.gatewayId} />
                            <InfoRow label="Gateway Version" value={gateway.softwareVersion} />
                            <InfoRow label="Coordinator (Jennic)" value={gateway.coordinatorVersion} />
                            <InfoRow label="Cabinet Controller" value={gateway.cabinetVersion} />
                            <InfoRow label="Chip Type" value={gateway.jennicChipType} />
                            <InfoRow
                                label="Last Seen"
                                value={gateway.lastSeen
                                    ? moment(gateway.lastSeen).format('DD-MM-YYYY HH:mm:ss')
                                    : null}
                            />
                            <InfoRow label="Total Devices" value={deviceCount} />
                        </Grid>
                    )}
                </Grid>

                {tab === 0 && (
                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <Typography variant="subtitle2">Start OTA</Typography>
                            <Box>
                                <Button
                                    size="small"
                                    variant={otaMode === 'devices' ? 'contained' : 'outlined'}
                                    onClick={() => setOtaMode('devices')}
                                    sx={{ mr: 0.5, textTransform: 'none' }}
                                >
                                    For Devices
                                </Button>
                                <Button
                                    size="small"
                                    variant={otaMode === 'gateway' ? 'contained' : 'outlined'}
                                    onClick={() => setOtaMode('gateway')}
                                    sx={{ textTransform: 'none' }}
                                >
                                    For Gateway
                                </Button>
                            </Box>
                        </Box>
                        {otaMode === 'devices' ? (
                            <GatewayOtaForm
                                gatewayId={gatewayId}
                                coordinatorVersion={gateway?.coordinatorVersion}
                            />
                        ) : (
                            <GwSelfOtaForm
                                gatewayId={gatewayId}
                                gateway={gateway}
                                coordinatorVersion={gateway?.coordinatorVersion}
                            />
                        )}
                    </Grid>
                )}
            </Grid>

            <Divider sx={{ my: 3 }} />
            
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Child Devices" />
                <Tab label="Device OTA Active" />
                <Tab label="Device OTA History" />
                <Tab label="GW OTA Active" />
                <Tab label="GW OTA History" />
            </Tabs>

            {tab === 0 && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                placeholder="Search by MAC (comma-separated)"
                                value={macInput}
                                onChange={e => setMacInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleMacSearch(); }}
                                sx={{ width: 300 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Button variant="contained" size="small" onClick={handleMacSearch}>Search</Button>
                            {macFilter.length > 0 && (
                                <Button variant="outlined" size="small" onClick={handleMacClear}>Clear</Button>
                            )}
                        </Box>
                        <TablePagination
                            component="div"
                            count={deviceTotal}
                            page={devicePage}
                            onPageChange={(_, p) => setDevicePage(p)}
                            rowsPerPage={devicePageSize}
                            onRowsPerPageChange={e => {
                                setDevicePageSize(parseInt(e.target.value, 10));
                                setDevicePage(0);
                            }}
                            rowsPerPageOptions={[10, 20, 50]}
                        />
                    </Box>
                    {loadingDevices ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <DataGrid
                            rows={devices}
                            columns={DEVICE_COLUMNS}
                            getRowId={(row) => row.id}
                            autoHeight
                            hideFooter
                            disableColumnMenu
                            disableSelectionOnClick
                            slots={{
                                noRowsOverlay: () => (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Typography color="text.secondary">No child devices</Typography>
                                    </Box>
                                )
                            }}
                        />
                    )}
                </>
            )}

            {tab === 1 && <GatewayOtaProgress gatewayId={gatewayId} coordinatorVersion={gateway?.coordinatorVersion} />}
            {tab === 2 && <GatewaySessionHistory gatewayId={gatewayId} coordinatorVersion={gateway?.coordinatorVersion} />}
            {tab === 3 && <GwOtaProgress gatewayId={gatewayId} />}
            {tab === 4 && <GwOtaHistory gatewayId={gatewayId} />}
        </Container>
    );
}

export default GatewayDetail;
